import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { getAssetUrl } from '../utils/assets';

interface TarotPull {
  card: {
    name: string;
    arcana: string;
    suit?: string;
    keywords: string[];
    reversedKeywords: string[];
    imageSlug: string;
  };
  reversed: boolean;
  position?: string;
  keywords: string[];
}

interface TarotSpread {
  type: string;
  cards: TarotPull[];
  formatted: string;
  pulledAt: string;
}

interface TarotPullPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onSendToChat?: (text: string) => void;
}

const TarotPullPanel: React.FC<TarotPullPanelProps> = ({ isOpen, onClose, onSendToChat }) => {
  const { t } = useTranslation();
  const [spread, setSpread] = useState<TarotSpread | null>(null);
  const [loading, setLoading] = useState(false);
  const [question, setQuestion] = useState('');

  const pullCards = async (type: 'single' | 'three-card' | 'celtic-cross') => {
    setLoading(true);
    try {
      const res = await axios.get(`/tarot/pull/${type}`);
      setSpread(res.data);
    } catch (err) {
      console.error('Tarot pull failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSendToChat = () => {
    if (spread && onSendToChat) {
      const questionLine = question.trim() ? `\n\n🕯️ Question: "${question.trim()}"\n` : '';
      const tarotInstruction = `\n\n[Tarot Reading Mode — Interpret this spread with emotional depth, intuition, and narrative resonance. Reference the querent's question if provided. Read each card's position, orientation (upright/reversed), and keywords as symbolic guidance. Be poetic, grounded, and personal — never generic or clinical.]`;
      onSendToChat(
        spread.formatted + questionLine + tarotInstruction + '\n\nPlease interpret this reading.',
      );
      setQuestion('');
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40" onClick={onClose} />
      <div
        className="fixed right-0 top-0 h-full w-80 max-w-[85vw] z-50 flex flex-col animate-slide-in-right"
        style={{
          background: 'linear-gradient(180deg, #1a1625 0%, #151221 100%)',
          borderLeft: '1px solid rgba(139, 92, 246, 0.15)',
          boxShadow: '-4px 0 32px rgba(0,0,0,0.6)',
        }}
      >
        <div className="flex items-center justify-between p-4 border-b border-dark-border dark:border-dark-border border-light-border">
          <h2 className="text-lg font-semibold text-dark-text dark:text-dark-text text-light-text">
            🔮 {t('tarot.title', 'Tarot Pull')}
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full hover:bg-dark-card dark:hover:bg-dark-card hover:bg-light-surface transition-colors"
          >
            <XMarkIcon className="w-5 h-5 text-dark-text-secondary" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Question Field */}
          <div>
            <label className="text-xs font-semibold uppercase text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary tracking-wider mb-1.5 block">
              🕯️ {t('tarot.questionLabel', "What's on your mind?")}
            </label>
            <textarea
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder={t(
                'tarot.questionPlaceholder',
                'Ask a question, set an intention, or leave blank for an open reading...',
              )}
              className="w-full px-3 py-2 rounded-lg bg-dark-card/50 dark:bg-dark-card/50 bg-light-surface/50 text-dark-text dark:text-dark-text text-light-text text-sm border border-dark-border/30 dark:border-dark-border/30 border-light-border/30 focus:border-primary-500 focus:outline-none resize-none"
              rows={2}
            />
          </div>

          {/* Spread Type Buttons */}
          <div className="space-y-2">
            <button
              onClick={() => pullCards('single')}
              disabled={loading}
              className="w-full py-3 px-4 rounded-lg bg-primary-500/20 hover:bg-primary-500/30 text-primary-400 font-medium transition-colors text-left"
            >
              <div className="text-sm font-semibold">🃏 {t('tarot.singlePull', 'Single Card')}</div>
              <div className="text-xs text-dark-text-secondary mt-0.5">
                {t('tarot.singleDesc', 'One card, one message')}
              </div>
            </button>
            <button
              onClick={() => pullCards('three-card')}
              disabled={loading}
              className="w-full py-3 px-4 rounded-lg bg-primary-500/20 hover:bg-primary-500/30 text-primary-400 font-medium transition-colors text-left"
            >
              <div className="text-sm font-semibold">
                🎴 {t('tarot.threeCard', 'Three-Card Spread')}
              </div>
              <div className="text-xs text-dark-text-secondary mt-0.5">
                {t('tarot.threeCardDesc', 'Past · Present · Future')}
              </div>
            </button>
            <button
              onClick={() => pullCards('celtic-cross')}
              disabled={loading}
              className="w-full py-3 px-4 rounded-lg bg-primary-500/20 hover:bg-primary-500/30 text-primary-400 font-medium transition-colors text-left"
            >
              <div className="text-sm font-semibold">
                ✨ {t('tarot.celticCross', 'Celtic Cross')}
              </div>
              <div className="text-xs text-dark-text-secondary mt-0.5">
                {t('tarot.celticCrossDesc', 'Full 10-card reading')}
              </div>
            </button>
          </div>

          {/* Loading */}
          {loading && (
            <div className="flex items-center justify-center py-8">
              <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
            </div>
          )}

          {/* Results */}
          {spread && !loading && (
            <div className="space-y-3">
              <div className="border-t border-dark-border dark:border-dark-border border-light-border pt-3">
                {spread.cards.map((pull, i) => (
                  <div
                    key={i}
                    className="mb-3 rounded-xl p-[1px]"
                    style={{
                      background:
                        'linear-gradient(135deg, rgba(168,85,247,0.9), rgba(56,189,248,0.4))',
                    }}
                  >
                    <div
                      className="rounded-[0.7rem] p-3"
                      style={{
                        background: 'rgba(15,23,42,0.96)',
                        boxShadow: '0 18px 40px rgba(15,23,42,0.75)',
                        backdropFilter: 'blur(18px)',
                      }}
                    >
                      {pull.position && (
                        <div className="text-xs font-semibold uppercase text-primary-400 mb-1 tracking-wider">
                          {pull.position}
                        </div>
                      )}
                      <div className="flex gap-3">
                        <img
                          src={getAssetUrl(`/tarot/${pull.card.imageSlug}.png`)}
                          alt={pull.card.name}
                          className={`w-16 h-auto rounded shadow-md ${pull.reversed ? 'rotate-180' : ''}`}
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-dark-text dark:text-dark-text text-light-text font-medium text-sm">
                              {pull.card.name}
                            </span>
                            <span
                              className={`text-xs px-1.5 py-0.5 rounded ${
                                pull.reversed
                                  ? 'bg-red-500/20 text-red-400'
                                  : 'bg-green-500/20 text-green-400'
                              }`}
                            >
                              {pull.reversed
                                ? t('tarot.reversed', 'Reversed')
                                : t('tarot.upright', 'Upright')}
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-1 mt-1.5">
                            {pull.keywords.map((kw, j) => (
                              <span
                                key={j}
                                className="text-xs bg-dark-bg/50 dark:bg-dark-bg/50 bg-light-bg/50 text-dark-text-secondary px-1.5 py-0.5 rounded"
                              >
                                {kw}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Send to Chat Button */}
              {onSendToChat && (
                <button
                  onClick={handleSendToChat}
                  className="w-full py-2.5 px-4 rounded-lg bg-primary-500 hover:bg-primary-600 text-white font-medium transition-colors text-sm"
                >
                  💬 {t('tarot.sendToChat', 'Send to Chat for Interpretation')}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default TarotPullPanel;
